import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminSidebar from "../SuperAdminSidebar";
import ManagerSidebar from "../ManagerSidebar";
import LeaderSidebar from "../LeaderSidebar";
import MemberSidebar from "../MemberSidebar";
import AdminNavbar from './AdminNavbar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEdit, faTrash, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import './Announcements.css';

const BASE = 'https://skcapi.btcjapan.co';
const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
};

const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Announcements = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewing, setViewing] = useState(null);   // ← the announcement object shown in the modal
    const userType = localStorage.getItem('usertype');

    const fetchAll = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${BASE}/get_announcements.php`, { headers: authHeader() });
            if (res.data.success) setRows(res.data.data);
            else setRows([]);
        } catch (err) {
            console.error('Failed to load announcements:', err);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const handleDelete = async (id) => {
        if (userType !== 'administrator') { alert('Only Super Admin can delete announcements.'); return; }
        if (!window.confirm('Are you sure you want to delete this announcement?')) return;
        try {
            const res = await axios.post(`${BASE}/delete_announcement.php`, { id }, {
                headers: { ...authHeader(), 'Content-Type': 'application/json' }
            });
            if (res.data.success) fetchAll();
            else alert(res.data.error || 'Delete failed');
        } catch (err) {
            alert('Delete failed');
        }
    };

    const filtered = rows.filter(r => {
        const term = searchTerm.toLowerCase();
        return !term
            || (r.title || '').toLowerCase().includes(term)
            || (r.created_by_name || '').toLowerCase().includes(term)
            || (r.status || '').toLowerCase().includes(term);
    });

    // Build a public URL for an attachment
    const attachUrl = (filename) =>
        `${BASE}/announcement_download.php?file=${encodeURIComponent(filename)}`;

    return (
        <div className="admin-container">
            <div className="sidebar-container">
                {userType === "administrator" && <SuperAdminSidebar />}
                {userType === "leader" && <LeaderSidebar />}
                {userType === "manager" && <ManagerSidebar />}
                {userType === "member" && <MemberSidebar />}
            </div>

            <div className="admin-content">
                <AdminNavbar />

                <div className="main-content">
                    <div className="ann-page">
                        <header className="ann-hero">
                            <div>
                                <h1>Announcements</h1>
                                <p>Manage announcements visible to your users and clients.</p>
                            </div>
                            <button
                                className="ann-new-btn"
                                onClick={() => navigate('/admin/announcements/new')}
                            >
                                <FontAwesomeIcon icon={faPlus} /> New Announcements
                            </button>
                        </header>

                        <div className="ann-card">
                            <div className="ann-card-head">
                                <h3>All Announcements</h3>
                            </div>

                            <div className="ann-toolbar">
                                <div className="ann-toolbar-left">
                                    <select className="toolbar-select" defaultValue={15}>
                                        <option>15</option>
                                        <option>30</option>
                                        <option>50</option>
                                    </select>
                                    <button className="toolbar-btn green">selected</button>
                                    <button className="toolbar-btn red" disabled>🗑</button>
                                    <button className="toolbar-btn blue" disabled>⬇</button>
                                    <button className="toolbar-btn blue" disabled>⬆</button>
                                </div>
                                <div className="ann-toolbar-search">
                                    <input
                                        type="text"
                                        placeholder="Search all columns:"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="table-responsive">
                                <table className="ann-table">
                                    <thead>
                                        <tr>
                                            <th>Title</th>
                                            <th>Created By</th>
                                            <th>Start Date</th>
                                            <th>End Date</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan="6" className="ann-empty">Loading announcements…</td></tr>
                                        ) : filtered.length === 0 ? (
                                            <tr><td colSpan="6" className="ann-empty">No announcements yet. Click "New Announcements" to create one.</td></tr>
                                        ) : (
                                            filtered.map(r => (
                                                <tr key={r.id}>
                                                    <td>
                                                        {/* Title now opens the modal, not a new tab */}
                                                        <button
                                                            className="ann-title-link"
                                                            onClick={() => setViewing(r)}
                                                            type="button"
                                                        >
                                                            {r.title}
                                                        </button>
                                                    </td>
                                                    <td>{r.created_by_name || '-'}</td>
                                                    <td>{formatDate(r.start_date)}</td>
                                                    <td>{formatDate(r.end_date)}</td>
                                                    <td>
                                                        <span className={`ann-badge ${r.status === 'Published' ? 'is-published' : 'is-unpublished'}`}>
                                                            {r.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="ann-actions">
                                                            <button
                                                                className="ann-icon-btn view"
                                                                onClick={() => setViewing(r)}
                                                                title="View details"
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button
                                                                className="ann-icon-btn edit"
                                                                onClick={() => navigate(`/admin/announcements/new?id=${r.id}`)}
                                                                title="Edit"
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button
                                                                className="ann-icon-btn delete"
                                                                onClick={() => handleDelete(r.id)}
                                                                disabled={userType !== 'administrator'}
                                                                title="Delete"
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="ann-footer">
                                <span>Showing {filtered.length} of {rows.length} Entries</span>
                                <div className="ann-pagination">
                                    <button disabled>Previous</button>
                                    <button className="is-active">1</button>
                                    <button disabled>Next</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* ============================================================
                ANNOUNCEMENT DETAILS MODAL
                ============================================================ */}
            {viewing && (
                <div className="ann-modal-overlay" onClick={() => setViewing(null)}>
                    <div className="ann-modal" onClick={(e) => e.stopPropagation()}>
                        <header className="ann-modal-head">
                            <h3>Announcement Details</h3>
                            <button
                                className="ann-modal-close"
                                onClick={() => setViewing(null)}
                                aria-label="Close"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </header>

                        <div className="ann-modal-body">
                            <Row label="Title" value={viewing.title} />
                            <Row label="Created By" value={viewing.created_by_name || '-'} />
                            <Row label="Start Date" value={formatDate(viewing.start_date)} />
                            <Row label="End Date" value={formatDate(viewing.end_date)} />
                            <Row
                                label="Share With"
                                value={viewing.share_clients ? 'All Clients' : 'Internal Only'}
                            />
                            <Row
                                label="Status"
                                value={
                                    <span className={`ann-badge ${viewing.status === 'Published' ? 'is-published' : 'is-unpublished'}`}>
                                        {viewing.status}
                                    </span>
                                }
                            />

                            <div className="ann-modal-section">
                                <div className="ann-modal-label">Description</div>
                                {/* Stored as plain text; render as paragraphs */}
                                <div className="ann-modal-description">
                                    {viewing.description
                                        ? viewing.description.split('\n').map((line, i) => (
                                            <p key={i}>{line || <br />}</p>
                                          ))
                                        : <em>No description</em>}
                                </div>
                            </div>

                            {viewing.attachment && (
                                <div className="ann-modal-section">
                                    <div className="ann-modal-label">Attachment</div>
                                    <div className="ann-modal-attach">
                                        {viewing.attach_type?.startsWith('image/') ? (
                                            <a
                                                href={attachUrl(viewing.attachment)}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <img
                                                    src={attachUrl(viewing.attachment)}
                                                    alt={viewing.attachment}
                                                    className="ann-modal-image"
                                                />
                                            </a>
                                        ) : (
                                            <a
                                                href={attachUrl(viewing.attachment)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="ann-modal-file"
                                            >
                                                📎 {viewing.attachment}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <footer className="ann-modal-foot">
                            <button
                                className="ann-btn-secondary"
                                onClick={() => setViewing(null)}
                            >
                                Close
                            </button>
                            <button
                                className="ann-btn-primary"
                                onClick={() => {
                                    const id = viewing.id;
                                    setViewing(null);
                                    navigate(`/admin/announcements/new?id=${id}`);
                                }}
                            >
                                Edit
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
};

// Small helper component for the modal's label/value rows
const Row = ({ label, value }) => (
    <div className="ann-modal-row">
        <div className="ann-modal-label">{label} :</div>
        <div className="ann-modal-value">{value}</div>
    </div>
);

export default Announcements;